import { motion } from "motion/react";
import { LuPlus, LuX, LuPhone } from "react-icons/lu";

export interface ContactMetadata {
  id?: string; // For existing contacts
  name: string;
  email: string;
  phoneNumber: string;
  description: string;
}

interface ContactsSectionProps {
  displayContacts: boolean;
  setDisplayContacts: (display: boolean) => void;
  contacts: ContactMetadata[];
  setContacts: (contacts: ContactMetadata[]) => void;
}

export default function ContactsSection({
  displayContacts,
  setDisplayContacts,
  contacts,
  setContacts,
}: ContactsSectionProps) {
  const addContact = () => {
    setContacts([
      ...contacts,
      {
        name: "",
        email: "",
        phoneNumber: "",
        description: "",
      },
    ]);
  };

  const removeContact = (index: number) => {
    const newContacts = [...contacts];
    newContacts.splice(index, 1);
    setContacts(newContacts);
    if (newContacts.length === 0) {
      setDisplayContacts(false);
    }
  };

  const updateContact = (
    index: number,
    field: "name" | "email" | "phoneNumber" | "description",
    value: string
  ) => {
    const newContacts = [...contacts];
    newContacts[index][field] = value;
    setContacts(newContacts);
  };

  if (displayContacts) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.3 }}
        className="border border-tertiary/20 rounded-md p-4"
      >
        <div className="flex flex-col gap-4">
          <div className="text-lg font-medium">Contacts</div>

          {contacts.length > 0 ? (
            <div className="space-y-4">
              {contacts.map((contact, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.3 }}
                  className="p-4 border border-border rounded-md bg-card/30"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="text-sm font-medium">
                      Contact {index + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeContact(index)}
                      className="text-tertiary hover:text-primary transition-colors cursor-pointer"
                    >
                      <LuX className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) =>
                          updateContact(index, "name", e.target.value)
                        }
                        className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
                        placeholder="Nom du contact"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={contact.email}
                        onChange={(e) =>
                          updateContact(index, "email", e.target.value)
                        }
                        className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
                        placeholder="Email du contact"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        value={contact.phoneNumber}
                        onChange={(e) =>
                          updateContact(index, "phoneNumber", e.target.value)
                        }
                        className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
                        placeholder="Numéro de téléphone"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">
                        Description
                      </label>
                      <textarea
                        value={contact.description}
                        onChange={(e) =>
                          updateContact(index, "description", e.target.value)
                        }
                        rows={2}
                        className="w-full p-2 rounded-md bg-input focus:outline-none focus:ring-2 focus:ring-card border border-border"
                        placeholder="Description du contact"
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <p className="text-secondary text-sm">Aucun contact ajouté</p>
          )}

          <button
            type="button"
            onClick={addContact}
            className="flex items-center gap-2 text-secondary hover:text-primary/80 transition-all duration-300 w-fit cursor-pointer"
          >
            <LuPlus className="w-4 h-4" />
            Ajouter un contact
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <div
      className="flex items-center gap-2 cursor-pointer text-secondary hover:text-primary/80 transition-all duration-300"
      onClick={() => {
        setDisplayContacts(true);
        // Add an empty contact when the section is first opened
        if (contacts.length === 0) {
          addContact();
        }
      }}
    >
      <LuPhone className="w-4 h-4" />
      Ajouter des <span className="underline">contacts</span>
    </div>
  );
}
